import { useEffect, useRef, useState } from "react";
import {
  CHART_HEIGHT_FACTOR_DEFAULT,
  CHART_HEIGHT_FACTOR_MAX,
  CHART_HEIGHT_FACTOR_MIN,
} from "../constants";
import { loadChartHeightFactor, saveChartHeightFactor } from "../settings/chartStorage";

const clampFactor = (factor) =>
  Math.min(CHART_HEIGHT_FACTOR_MAX, Math.max(CHART_HEIGHT_FACTOR_MIN, factor));

const loadFactor = () => clampFactor(loadChartHeightFactor() ?? CHART_HEIGHT_FACTOR_DEFAULT);

/**
 * Chart height set with the resize handle under the time axis: a factor of the
 * height the page CSS gives the chart, remembered in this browser.
 * The engine observes its container, so the canvas follows by itself.
 *
 * @param {React.RefObject<HTMLElement>} chartRef - the element whose height the factor scales
 * @returns {{ factor: number, isDragging: boolean, handleProps: object }} handleProps go on the handle
 */
const useChartHeightFactor = (chartRef) => {
  const [factor, setFactor] = useState(loadFactor);
  const [isDragging, setIsDragging] = useState(false);
  // Drag start: pointer Y, factor and the height of the chart at factor 1
  const dragRef = useRef(null);

  useEffect(() => {
    saveChartHeightFactor(factor);
  }, [factor]);

  const onPointerDown = (event) => {
    if (event.button !== 0 || !chartRef.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startY: event.clientY,
      startFactor: factor,
      baseHeight: chartRef.current.getBoundingClientRect().height / factor,
    };
    setIsDragging(true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || !drag.baseHeight) return;
    setFactor(clampFactor(drag.startFactor + (event.clientY - drag.startY) / drag.baseHeight));
  };

  const endDrag = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  return {
    factor,
    isDragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick: () => setFactor(CHART_HEIGHT_FACTOR_DEFAULT),
    },
  };
};

export default useChartHeightFactor;
